// 060 — Lista de Verificación Cirugía Segura (Anexo 1 MSP, SNS-MSP/HCU-form.060/2021). Fase 7, Tanda 8: bloques B-F
// del papel con cada fila SI / NO / NO PROCEDE (o NO APLICA) distinguible de "sin responder". Campos y decisiones en
// `bloques060Datos.ts`. El bloque A lo resuelve `EncabezadoMspCampos`. Sin FHIR: queda como `Par[]` en el PDF firmado.
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import { Bloque, CamposSimples } from './camposSimples';
import type { CampoSimple } from './camposSimplesDatos';
import { AreaDictado } from './AreaDictado';
import {
  CAMPOS_CIRUGIA_060,
  CAMPOS_ENTRADA_060,
  CAMPOS_ENTRADA_CONFIRMADO_060,
  CAMPOS_PAUSA_060,
  CAMPOS_RESPONSABLES_060,
  CAMPOS_SALIDA_060,
  estadoInicial060,
  itemsSinResponder060,
  paresDe060,
  type Estado060,
} from './bloques060Datos';

const CODIGO = '060';
const sinAreas = (campos: readonly CampoSimple[]): readonly CampoSimple[] => campos.filter((c) => c.tipo !== 'area');
const areas = (campos: readonly CampoSimple[]): readonly CampoSimple[] => campos.filter((c) => c.tipo === 'area');

export function Formulario060({ subject, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const { guardando, error, guardar } = useGuardado();
  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const titulo = t('hub.formulario.060');
  // Servicio del encabezado del PDF (no el 'Consulta externa' por defecto): Lista de verificación de cirugía segura: el papel no trae servicio (trae QUIRÓFANO); es quirúrgico.
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, CODIGO, titulo, { servicio: 'Quirófano' });
  const [estado, setEstado] = useState<Estado060>(() => estadoInicial060());

  const pendientes = itemsSinResponder060(estado);
  const sinResponder = pendientes.length;
  /** Conjunto de ítems sin responder ya avisado: con el mismo conjunto, el segundo clic continúa (el aviso no bloquea). */
  const [avisado, setAvisado] = useState<string | null>(null);
  const claveAviso = pendientes.join('|');
  const yaAvisado = sinResponder > 0 && avisado === claveAviso;

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      if (sinResponder > 0 && !yaAvisado) {
        setAvisado(claveAviso);
        return;
      }
      onGuardado(paresDe060(estado), datosEncabezadoMsp);
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{titulo}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <Bloque titulo={t('formularios.f060.cirugiaTitulo')}>
        <CamposSimples prefijoId="f060-cir" campos={CAMPOS_CIRUGIA_060} valores={estado.cirugia} columnas={3} onChange={(v) => setEstado({ ...estado, cirugia: v })} />
      </Bloque>

      <Bloque titulo={t('formularios.f060.entradaTitulo')}>
        <CamposSimples prefijoId="f060-conf" campos={CAMPOS_ENTRADA_CONFIRMADO_060} valores={estado.confirmado} columnas={2} onChange={(v) => setEstado({ ...estado, confirmado: v })} />
        <CamposSimples prefijoId="f060-ent" campos={CAMPOS_ENTRADA_060} valores={estado.entrada} columnas={2} onChange={(v) => setEstado({ ...estado, entrada: v })} />
      </Bloque>

      <Bloque titulo={t('formularios.f060.pausaTitulo')}>
        <CamposSimples prefijoId="f060-pau" campos={CAMPOS_PAUSA_060} valores={estado.pausa} columnas={2} onChange={(v) => setEstado({ ...estado, pausa: v })} />
      </Bloque>

      <Bloque titulo={t('formularios.f060.salidaTitulo')}>
        <CamposSimples prefijoId="f060-sal" campos={sinAreas(CAMPOS_SALIDA_060)} valores={estado.salida} columnas={2} onChange={(v) => setEstado({ ...estado, salida: { ...estado.salida, ...v } })} />
        {areas(CAMPOS_SALIDA_060).map((c) => (
          <AreaDictado key={c.clave} id={`f060-sal-${c.clave}`} etiqueta={c.etiqueta} valor={estado.salida[c.clave] ?? ''} filas={3} onChange={(v) => setEstado({ ...estado, salida: { ...estado.salida, [c.clave]: v } })} />
        ))}
      </Bloque>

      <Bloque titulo={t('formularios.f060.responsablesTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f060.responsablesNota')}</p>
        <CamposSimples prefijoId="f060-resp" campos={CAMPOS_RESPONSABLES_060} valores={estado.responsables} columnas={3} onChange={(v) => setEstado({ ...estado, responsables: v })} />
      </Bloque>

      <ErrorGuardado error={error} />

      {yaAvisado ? (
        <Alert variant="orange">
          <AlertDescription>{t('formularios.f060.avisoSinResponder', { count: sinResponder })}</AlertDescription>
        </Alert>
      ) : null}

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {yaAvisado ? t('formularios.f060.continuarSinResponder') : t('formulario.guardar')}
      </Button>
    </div>
  );
}
